// frontend/src/landing/components/ScreenshotSlideshow.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

type Slide = {
  id: string;
  title: string;
  caption?: string;
  alt: string;
  kind?: "image" | "video";

  // image
  lightSrc?: string;
  darkSrc?: string;

  // video
  mp4LightSrc?: string;
  mp4DarkSrc?: string;
};

type Props = {
  slides: Slide[];
  intervalMs?: number; // autoplay interval for IMAGE slides
  initialIndex?: number;
  className?: string;
};

function usePrefersDark(): boolean {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false;
  });

  useEffect(() => {
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;
    const onChange = () => setIsDark(mql.matches);
    // Safari: addListener/removeListener fallback
    if ("addEventListener" in mql) mql.addEventListener("change", onChange);
    else (mql as any).addListener(onChange);
    return () => {
      if ("removeEventListener" in mql) mql.removeEventListener("change", onChange);
      else (mql as any).removeListener(onChange);
    };
  }, []);

  return isDark;
}

export default function ScreenshotSlideshow({
  slides,
  intervalMs = 7000,
  initialIndex = 0,
  className,
}: Props) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const prefersDark = usePrefersDark();

  const safeSlides = useMemo(() => slides.filter(Boolean), [slides]);
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(0, initialIndex), Math.max(0, safeSlides.length - 1))
  );
  const [isPaused, setPaused] = useState(false);
  const timerRef = useRef<number | null>(null);

  const canAutoplay = !prefersReducedMotion && safeSlides.length > 1;

  const go = (next: number) => {
    const n = safeSlides.length;
    if (n === 0) return;
    const i = ((next % n) + n) % n;
    setIndex(i);
  };

  const next = () => go(index + 1);
  const prev = () => go(index - 1);

  // Autoplay: nur für Images. Video läuft bis onEnded -> next().
  useEffect(() => {
    if (!canAutoplay || isPaused) return;
    if (safeSlides.length === 0) return;

    const current = safeSlides[index];
    const isVideo = current?.kind === "video";

    // Bei Video keinen Timer starten
    if (isVideo) return;

    timerRef.current = window.setTimeout(() => {
      setIndex((i) => (i + 1) % safeSlides.length);
    }, intervalMs);

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [canAutoplay, isPaused, index, intervalMs, safeSlides]);

  // Keyboard support when focused
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (safeSlides.length <= 1) return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    }
  };

  if (safeSlides.length === 0) return null;

  const current = safeSlides[index];
  const isVideo = current.kind === "video";
  const currentImgSrc = prefersDark ? current.darkSrc : current.lightSrc;

  return (
    <div
      className={`zg-slideshow ${className ?? ""}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={onKeyDown}
      tabIndex={0}
      aria-roledescription="carousel"
      aria-label="Produkt-Screenshots"
    >
      {/* Viewport (16:10) */}
      <div className="zg-slides" aria-live="polite">
        {isVideo ? (
          <video
            key={current.id}
            className="zg-slide-img"
            autoPlay
            muted
            playsInline
            preload="metadata"
            onEnded={() => go(index + 1)}
          >
            {(() => {
              const vsrc = prefersDark ? current.mp4DarkSrc : current.mp4LightSrc;
              return vsrc ? <source src={vsrc} type="video/mp4" /> : null;
            })()}
          </video>
        ) : (
          <img className="zg-slide-img" src={currentImgSrc} alt={current.alt} loading="eager" />
        )}
      </div>

      {/* Caption unter dem Bild */}
      <div className="zg-slide-caption">
        <div className="zg-slide-title">{current.title}</div>
        {current.caption ? <div className="zg-slide-text">{current.caption}</div> : null}
      </div>

      {/* Controls */}
      {safeSlides.length > 1 ? (
        <div className="zg-slide-controls">
          <button className="zg-slide-btn" type="button" onClick={prev} aria-label="Vorheriger Screenshot">
            ←
          </button>

          <div className="zg-slide-dots" role="tablist" aria-label="Screenshot-Auswahl">
            {safeSlides.map((s, i) => (
              <button
                key={s.id}
                className={`zg-dotbtn ${i === index ? "is-active" : ""}`}
                type="button"
                onClick={() => go(i)}
                aria-label={`Screenshot ${i + 1}: ${s.title}`}
                aria-current={i === index ? "true" : "false"}
              />
            ))}
          </div>

          <button className="zg-slide-btn" type="button" onClick={next} aria-label="Nächster Screenshot">
            →
          </button>
        </div>
      ) : null}
    </div>
  );
}